import type { Bbox, PriceUnit, ProduceStatus } from '@/types/domain';

// v3: the form gained a price unit. Bumping discards older drafts
// rather than restoring one that has no unit in it.
// v4: the location became a province/district/neighbourhood
// selection made of ids. A v3 draft holds a district NAME where an id is now
// needed, so it must be discarded rather than half-restored.
const VERSION = 4;
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * A create-mode listing form parked in localStorage. Photos are deliberately
 * absent: DraftPhoto holds a real File plus an object URL, neither of which
 * survives JSON — persisting them would mean IndexedDB. `photoCount` is written
 * but currently read by nothing: it is the only trace that a restored draft once
 * had photos, and the one hook for ever telling the producer so.
 *
 * This is the app's first client-side persistence of any kind (auth is an
 * httpOnly cookie), so it is deliberately narrow and self-expiring.
 */
export interface ListingDraft {
  v: number;
  savedAt: number;
  photoCount: number;
  title: string;
  categoryId: string;
  productId: string;
  /** Absent in drafts saved before the field existed. */
  customProductName?: string;
  productType: string;
  quantity: string;
  quantityUnit: PriceUnit;
  price: string;
  priceUnit: PriceUnit;
  status: ProduceStatus;
  countyCode?: number;
  city?: string;
  townId?: number;
  district?: string;
  neighbourhoodId?: number;
  neighbourhood?: string;
  /** Extent of the deepest level chosen, so a restored draft frames its map as it was left. */
  bbox?: Bbox;
  /** No pin is a normal draft — the listing then falls back to its neighbourhood's centre. */
  coords: { lat: number; lon: number } | null;
  harvestYear: string;
  harvestRange: string;
  description: string;
  durationMonths: 1 | 3 | 6;
}

export type ListingDraftInput = Omit<ListingDraft, 'v' | 'savedAt'>;

const key = (userId: string) => `cb.draft.listing.${userId}.new`;

export function saveDraft(userId: string, input: ListingDraftInput): void {
  try {
    const payload: ListingDraft = { ...input, v: VERSION, savedAt: Date.now() };
    localStorage.setItem(key(userId), JSON.stringify(payload));
  } catch {
    // Quota exceeded, Safari private mode, storage disabled by policy. A draft
    // that cannot be saved must never break the form being filled in.
  }
}

export function loadDraft(userId: string): ListingDraft | null {
  try {
    const raw = localStorage.getItem(key(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ListingDraft>;
    // Shape change or stale: discard rather than half-restore a form.
    if (
      parsed.v !== VERSION ||
      typeof parsed.savedAt !== 'number' ||
      Date.now() - parsed.savedAt > MAX_AGE_MS
    ) {
      clearDraft(userId);
      return null;
    }
    return parsed as ListingDraft;
  } catch {
    clearDraft(userId); // unparseable payload
    return null;
  }
}

export function hasDraft(userId: string): boolean {
  return loadDraft(userId) !== null;
}

export function clearDraft(userId: string): void {
  try {
    localStorage.removeItem(key(userId));
  } catch {
    // nothing useful to do — the caller is mid-logout or mid-submit either way
  }
}
